/**
 * How It Works Modal
 *
 * First-visit onboarding modal with clickwrap assent for terms and age verification.
 * Shows once per device using a cookie. Cannot be dismissed except by clicking the button.
 */

import { useState, useEffect } from 'react';
import { Zap, Bot, Coins } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { hasAcknowledgedHowItWorks, setHowItWorksAcknowledged } from '@/lib/cookies';
import { logConsent } from '@/lib/consent-logger';
import { useTurnkeyWallet } from '@/providers/TurnkeyProvider';
import { debug } from "@/lib/debug";

export function HowItWorksModal() {
  const [open, setOpen] = useState(false);
  const { walletAddress } = useTurnkeyWallet();

  useEffect(() => {
    if (!hasAcknowledgedHowItWorks()) {
      setOpen(true);
      debug.trace('[HowItWorks] Modal shown - user not acknowledged');
    }
  }, []);

  const handleAcknowledge = () => {
    setHowItWorksAcknowledged();
    setOpen(false);
    // Fire-and-forget: audit log is non-blocking; cookie + modal close happen synchronously above
    logConsent(walletAddress).catch(() => {});
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="!mx-0 w-[calc(100%-32px)] sm:w-full sm:max-w-md bg-background border-border px-6 overflow-y-auto"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        hideCloseButton
      >
        <DialogHeader>
          <DialogTitle className="text-center text-xl font-semibold">
            Why launch on dextoro?
          </DialogTitle>
        </DialogHeader>

        <div className="text-center text-sm text-muted-foreground">
          dextoro is the first launchpad with a built-in{' '}
          <span className="text-[#CCFF00]">AI market maker</span>.
          every coin gets smarter liquidity, better charts, and{' '}
          <span className="text-[#CCFF00]">creator earnings</span>{' '}
          from day one.
        </div>

        <div className="space-y-4 text-sm">
          <div className="flex items-start gap-3">
            <Zap className="h-5 w-5 text-[#CCFF00] shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-foreground">Launch a coin in seconds</p>
              <p className="text-muted-foreground">no code, no contracts, fair-launch</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Bot className="h-5 w-5 text-[#CCFF00] shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-foreground">Built-in AI market maker</p>
              <p className="text-muted-foreground">smarter liquidity, better price discovery</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Coins className="h-5 w-5 text-[#CCFF00] shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-foreground">Earn as a creator</p>
              <p className="text-muted-foreground">1.4% of all trading volume, paid in SOL</p>
            </div>
          </div>
        </div>

        {/* If you change this consent text, also update CONSENT_TEXT in src/lib/consent-logger.ts */}
        <p className="text-xs text-muted-foreground text-center">
          By clicking this button, you agree to the terms and conditions and
          certify that you are over 18 years old.
        </p>

        <Button
          onClick={handleAcknowledge}
          className="w-full min-h-[44px] bg-[#CCFF00] text-black hover:bg-[#b8e600] font-semibold"
          autoFocus
        >
          I'm ready to launch
        </Button>

        <div className="text-xs text-muted-foreground text-center">
          <a
            href="/how-it-works"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline hover:text-foreground transition-colors"
          >
            How It Works
          </a>
          <span className="mx-1 text-muted-foreground/50">·</span>
          <a
            href="https://dextoro.com/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline hover:text-foreground transition-colors"
          >
            Terms of Service
          </a>
          <span className="mx-1 text-muted-foreground/50">·</span>
          <a
            href="https://dextoro.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline hover:text-foreground transition-colors"
          >
            Privacy Policy
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
}
